import { useState } from 'react';

import { FormField, Link, Modal, ModalFooterButton, SectionMessage } from '@kc-console/common-ui';
import {
  CONSOLE_VERSION,
  env,
  type ModalComponentProps,
  usePatchUserSettings,
  useToast,
  useUserPreferenceQuery,
} from '@kc-console/context';
import { useTranslation } from '@kc-console/core';
import { EnabledSwitch } from '@kc-console/shared-ui';

type UpdateConsoleVersionModalProps = ModalComponentProps;

const UpdateConsoleVersionModal = (props: UpdateConsoleVersionModalProps) => {
  const { t } = useTranslation();
  const { data } = useUserPreferenceQuery();
  const { onClose, open = false, TransitionProps } = props;
  const { mutateAsync, isPending } = usePatchUserSettings();
  const { enqueueSuccessToast, enqueueErrorToast } = useToast();

  const originalEnabled = data?.consoleVersion === CONSOLE_VERSION.BD;

  const [enabled, setEnabled] = useState<boolean>(originalEnabled);

  const handleSubmit = async () => {
    if (enabled === originalEnabled) {
      onClose();
      return;
    }

    try {
      await mutateAsync({ consoleVersion: enabled ? CONSOLE_VERSION.BD : CONSOLE_VERSION.UR });
      enqueueSuccessToast(t('layout:console_version.update.success_msg'));
      window.location.reload();

      onClose();
    } catch (error) {
      enqueueErrorToast(t('layout:console_version.update.err_msg.default'));
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => !isPending && onClose()}
      size="sm"
      loading={isPending}
      slotProps={{
        transition: TransitionProps,
        header: {
          title: t('layout:console_version.modal.update.title'),
          description: t('layout:console_version.modal.update.description'),
        },
        body: {
          children: (
            <>
              <FormField label={t('layout:account.console_version.new_console.label')}>
                <EnabledSwitch checked={enabled} onChange={(_, value) => setEnabled(value)} />
              </FormField>
              <SectionMessage variant="information">
                {t('layout:console_version.modal.update.message')}
                <br />
                {t('layout:console_version.modal.update.message_link_description')}
                <Link href={env.KC_NEW_CONSOLE_GUIDE_LINK ?? ''} external>
                  {t('layout:console_version.modal.update.message_link')}
                </Link>
              </SectionMessage>
            </>
          ),
        },
        footer: {
          labelMap: { submit: t('common:common.action.save') },
          buttons: [
            <ModalFooterButton
              key="update-console-version-modal-cancel-button"
              eventType="cancel"
              onClick={() => !isPending && onClose()}
            />,
            <ModalFooterButton
              key="update-console-version-modal-submit-button"
              eventType="submit"
              onClick={handleSubmit}
            />,
          ],
        },
      }}
    />
  );
};

export default UpdateConsoleVersionModal;
